import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { API_URL } from '../../config';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const PresenceAvatars = ({ documentId, currentUserId }) => {
  const [presence, setPresence] = useState([]);
  const intervalRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    
    // Update presence immediately and then every 5 seconds
    const updatePresence = async () => {
      if (!documentId || !isMounted.current) return;
      
      try {
        // Post our presence
        await axios.post(`${API_URL}/documents/${documentId}/presence`);
        
        // Get all active users
        const response = await axios.get(`${API_URL}/documents/${documentId}/presence`);
        if (isMounted.current) {
          // Filter out current user
          const others = response.data.filter(p => p.user_id !== currentUserId);
          setPresence(others);
        }
      } catch (error) {
        console.error('Failed to update presence:', error);
      }
    };

    updatePresence();
    intervalRef.current = setInterval(updatePresence, 5000);

    return () => {
      isMounted.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Leave document on unmount
      if (documentId) {
        axios.delete(`${API_URL}/documents/${documentId}/presence`).catch(() => {});
      }
    };
  }, [documentId, currentUserId]);

  if (presence.length === 0) {
    return null;
  }

  const displayAvatars = presence.slice(0, 3);
  const remainingCount = presence.length - 3;

  return (
    <TooltipProvider>
      <div className="flex items-center -space-x-2" data-testid="presence-avatars">
        {displayAvatars.map((user) => (
          <Tooltip key={user.user_id}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                  'ring-2 ring-white dark:ring-slate-900 cursor-default'
                )}
                style={{ backgroundColor: user.avatar_color }}
                data-testid={`presence-avatar-${user.user_id}`}
              >
                {user.user_name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{user.user_name}</p>
              <p className="text-xs text-slate-400">Viewing now</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center',
                  'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
                  'text-xs font-medium ring-2 ring-white dark:ring-slate-900 cursor-default'
                )}
              >
                +{remainingCount}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{remainingCount} more viewing</p>
            </TooltipContent>
          </Tooltip>
        )}
        <span className="ml-3 text-sm text-slate-500 dark:text-slate-400">
          {presence.length === 1 ? '1 other viewing' : `${presence.length} others viewing`}
        </span>
      </div>
    </TooltipProvider>
  );
};

export default PresenceAvatars;
